import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCarousel } from '@/hooks/useCarousel';
import { useI18n } from '@/hooks/useI18n';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { useShell } from '@/hooks/useShell';
import { getCabinetRouteForUser, getRouteForRoleOrCategory } from '@/lib/roleRoutes';
import {
  cabinetNavigateOptions,
  isWorkspaceCabinetPath,
  readReturnTo,
} from '@/lib/navigationState';
import { routes } from '@/lib/routes';
import { ownerFocusSlides, slides } from '../data';

export const HeroSlider = () => {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { shell } = useShell();
  const { user } = useAuth();
  const reducedMotion = usePrefersReducedMotion();

  const activeRole = shell?.roles.find((role) => role.key === user?.activeRole);
  const roleFocus = Boolean(user && activeRole);
  const isOwnerFocus = Boolean(user && user.activeRole === 'SYSTEM_OWNER');
  const remembered = readReturnTo();
  const roleHome = user
    ? remembered && isWorkspaceCabinetPath(remembered)
      ? remembered
      : getCabinetRouteForUser(user)
    : getRouteForRoleOrCategory(activeRole?.key) ?? routes.home;

  const openCabinet = (href: string, role?: string) => {
    navigate(
      href,
      cabinetNavigateOptions(href, {
        role: role ?? user?.activeRole,
      }),
    );
  };

  const apiSlides = shell?.slides ?? [];
  const ctaPrimaryKeys = ['slide.1.primary', 'slide.2.primary', 'slide.3.primary'] as const;
  const ctaSecondaryKeys = ['slide.1.secondary', 'slide.2.secondary', 'slide.3.secondary'] as const;
  const items = isOwnerFocus
    ? ownerFocusSlides.map((slide) => ({
        id: slide.id,
        kicker: slide.kicker,
        title: slide.title,
        text: slide.text,
        accentA: slide.accentA,
        accentB: slide.accentB,
        primaryTo: slide.primaryTo,
        secondaryTo: routes.assistant,
        primaryLabel: 'Открыть',
        secondaryLabel: 'AI-консультант',
      }))
    : apiSlides.length > 0
      ? apiSlides.map((slide, slideIndex) => ({
          id: slide.id,
          kicker: slide.kicker,
          title: slide.title,
          text: slide.text,
          accentA: slide.accent_a,
          accentB: slide.accent_b,
          primaryTo: slide.href || (user ? getCabinetRouteForUser(user) : routes.login),
          secondaryTo: routes.assistant,
          primaryLabel: t(ctaPrimaryKeys[slideIndex % ctaPrimaryKeys.length] ?? 'slide.cta.cabinet'),
          secondaryLabel: t(
            ctaSecondaryKeys[slideIndex % ctaSecondaryKeys.length] ?? 'slide.cta.schedule',
          ),
        }))
      : slides.map((slide) => ({
          id: slide.id,
          kicker: t(slide.kickerKey),
          title: t(slide.titleKey),
          text: t(slide.textKey),
          accentA: slide.accentA,
          accentB: slide.accentB,
          primaryTo: slide.primaryTo,
          secondaryTo: slide.secondaryTo,
          primaryLabel: t(slide.primaryKey),
          secondaryLabel: t(slide.secondaryKey),
        }));

  const { index, goTo } = useCarousel({
    length: Math.max(items.length, 1),
    paused: reducedMotion || items.length === 0,
  });

  if (roleFocus && activeRole && items.length === 0) {
    return (
      <main
        className="panel hero-slider hero-slider--role"
        style={{ '--role-accent': activeRole.accent } as CSSProperties}
        aria-label={activeRole.label[locale]}
      >
        <div className="role-stage-glow" aria-hidden="true" />
        <div className="role-stage-copy">
          <span className="role-stage-eyebrow">{activeRole.label[locale]}</span>
          <h2>{activeRole.caption[locale] || t('home.side.title')}</h2>
          <p>{shell?.hero?.aside_text?.[locale] || t('home.side.text')}</p>
          <div className="slide-actions">
            <button type="button" className="btn-primary" onClick={() => openCabinet(roleHome)}>
              Открыть
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(routes.assistant)}
            >
              AI-консультант
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      className={`panel hero-slider${roleFocus ? ' hero-slider--role' : ''}`}
      style={roleFocus && activeRole ? ({ '--role-accent': activeRole.accent } as CSSProperties) : undefined}
      aria-roledescription="carousel"
      aria-label={t('a11y.slider')}
    >
      {roleFocus ? <div className="role-stage-glow" aria-hidden="true" /> : null}
      {items.map((slide, position) => (
        <article
          key={slide.id}
          className="slide"
          data-active={position === index}
          aria-hidden={position !== index}
          style={{ '--a': slide.accentA, '--b': slide.accentB } as CSSProperties}
        >
          <div className="slide-content">
            <span className="slide-kicker">
              {roleFocus && activeRole ? activeRole.label[locale] : slide.kicker}
            </span>
            <h2>{slide.title}</h2>
            <p>{slide.text}</p>
            <div className="slide-actions">
              <button
                type="button"
                className="btn-primary"
                tabIndex={position === index ? 0 : -1}
                onClick={() =>
                  openCabinet(roleFocus ? roleHome : slide.primaryTo, user?.activeRole)
                }
              >
                {roleFocus ? 'Открыть' : slide.primaryLabel}
              </button>
              <button
                type="button"
                className="btn-secondary"
                tabIndex={position === index ? 0 : -1}
                onClick={() => navigate(slide.secondaryTo)}
              >
                {roleFocus ? 'AI-консультант' : slide.secondaryLabel}
              </button>
            </div>
          </div>
        </article>
      ))}

      <div className="slider-dots">
        {items.map((slide, position) => (
          <button
            key={slide.id}
            type="button"
            aria-current={position === index}
            aria-label={`${t('a11y.gotoSlide')} ${position + 1}`}
            onClick={() => goTo(position)}
          />
        ))}
      </div>
    </main>
  );
};
